import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { MyBillOverviewContainerComponent } from './my-bill/overview/my-bill-overview-container.component';
import { CallDetailsTableComponent } from './my-bill/call-details/components/call-details-table/call-details-table.component';
import { AnalyticsContextResolverService } from './guards/analytics-context-resolver.service';
import { AnalyticsMessageInfoResolverService } from './guards/analytics-message-info-resolver.service';
import { CBUOnlyGuard } from './guards/cbu-only.guard';
import { MyProximusAuthenticationCheckGuard } from './guards/myproximus-authentication-check.guard';
import { CoverPageComponent } from './my-bill/one-digital-bill/cover-page.component';
import { CoverNoDetailsPageComponent } from './my-bill/one-digital-bill/cover-no-details-page.component';
import { OdbCategoryDetailsPageComponent } from './my-bill/one-digital-bill/odb-category-details-page.component';
import { OdbThirdPartyLevelThreePageComponent } from './my-bill/one-digital-bill/category-details/odb-third-party-level-three-page.component';
import { OdbThirdPartyLevel4PageComponent } from './my-bill/one-digital-bill/category-details/odb-third-party-level-4-page.component';
import { OdbUsageLevelThreePageComponent } from './my-bill/one-digital-bill/odb-usage-level-three-page.component';
import { OdbUsageLevelFourPageComponent } from './my-bill/one-digital-bill/odb-usage-level-four-page.component';
import { DeferralLandingPageComponent } from './my-bill/deferral/deferral-landing-page.component';
import { BillOptionsPaymentFacilitiesInfoPageComponent } from './my-bill/billing-options/bill-options-payment-facilities-info-page.component';
import { DeferralSelectDatePageComponent } from './my-bill/deferral/deferral-select-date-page.component';
import { DeferralAcceptNewDatePageComponent } from './my-bill/deferral/deferral-accept-new-date-page.component';
import { DeferralSuccessfulNewDatePageComponent } from './my-bill/deferral/deferral-successful-new-date-page.component';
import { MyBillLandingContainerComponent } from './my-bill/overview/my-bill-landing-container.component';
import { MyBillInstalmentPlanDetailsPageComponent } from './my-bill/instalment-plan/my-bill-instalment-plan-details-page.component';
import { MyBillEvolutionChartPageComponent } from './my-bill/evolution/my-bill-evolution-chart-page.component';
import { ExplanationComponent } from './my-bill/one-digital-bill/explanations/explanation.component';
import { ExpiredTokenComponent } from './my-bill/common/expired-token/expired-token.component';

const routes: Routes = [
  {
    path: 'overview',
    component: MyBillLandingContainerComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
    data: {
      analytics: {
        page: {
          id: 'mybill.overview',
          name: 'mybill.overview',
          category: {
            l1: 'myproximus',
            l2: 'my-bill',
            l3: 'billing.overview',
          },
        },
      },
    },
  },
  {
    path: 'overview/fls/:billingAccountNumber',
    component: MyBillOverviewContainerComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
    resolve: {
      AnalyticsInfoMessage: AnalyticsMessageInfoResolverService,
    },
    data: {
      analytics: {
        page: {
          id: 'mybill.overview',
          name: 'mybill.overview',
          category: {
            l1: 'myproximus',
            l2: 'my-bill',
            l3: 'billing.overview',
          },
        },
      },
    },
  },
  {
    // The overview/FLS path is used for smooth access
    path: 'overview/FLS/:billingAccountNumber',
    component: MyBillOverviewContainerComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
    resolve: {
      AnalyticsInfoMessage: AnalyticsMessageInfoResolverService,
    },
    data: {
      analytics: {
        page: {
          id: 'mybill.overview',
          name: 'mybill.overview',
          category: {
            l1: 'myproximus',
            l2: 'my-bill',
            l3: 'billing.overview',
            l4: 'smoothaccess',
          },
        },
      },
    },
  },
  {
    path: 'billing-explanation/:explanationType',
    component: ExplanationComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
  },
  // TODO(BUPA-5646): avoid PII in the URL; make sure it still works for mobile which requires currently url params
  {
    path: 'billing-summary/fls/:billingAccountNumber/:documentId',
    component: CallDetailsTableComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
    resolve: {
      AnalyticsContext: AnalyticsContextResolverService,
      AnalyticsInfoMessage: AnalyticsMessageInfoResolverService,
    },
    data: {
      analytics: {
        context: '', //Note: injected dynamically by the resolver
        page: {
          id: 'mybill.detail',
          name: 'mybill.detail.table',
          category: {
            l1: 'myproximus',
            l2: 'my-bill',
            l3: 'billing.detail.table',
          },
        },
      },
    },
  },

  {
    path: 'cover',
    component: CoverPageComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
    resolve: {
      AnalyticsContext: AnalyticsContextResolverService,
      AnalyticsInfoMessage: AnalyticsMessageInfoResolverService,
    },
    data: {
      analytics: {
        context: '', //Note: injected dynamically by the resolver
        page: {
          id: 'mybill.detail',
          name: 'mybill.detail',
          category: {
            l1: 'myproximus',
            l2: 'my-bill',
            l3: 'billing.detail',
          },
        },
      },
    },
  },
  {
    path: 'coverNoDetails',
    component: CoverNoDetailsPageComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
    resolve: {
      AnalyticsContext: AnalyticsContextResolverService,
      AnalyticsInfoMessage: AnalyticsMessageInfoResolverService,
    },
    data: {
      analytics: {
        context: '', //Note: injected dynamically by the resolver
        page: {
          id: 'mybill.detail',
          name: 'mybill.detail',
          category: {
            l1: 'myproximus',
            l2: 'my-bill',
            l3: 'billing.detail',
          },
        },
      },
    },
  },
  {
    path: 'evolution',
    component: MyBillEvolutionChartPageComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
    resolve: {
      AnalyticsInfoMessage: AnalyticsMessageInfoResolverService,
    },
    data: {
      analytics: {
        page: {
          id: 'mybill.evolution',
          name: 'mybill.evolution',
          category: {
            l1: 'myproximus',
            l2: 'my-bill',
            l3: 'mybill.evolution',
          },
        },
      },
    },
  },
  {
    path: 'odbCategoryDetails',
    component: OdbCategoryDetailsPageComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
  },
  {
    path: 'usageL4',
    component: OdbUsageLevelFourPageComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
  },
  {
    path: 'usageL3',
    component: OdbUsageLevelThreePageComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
  },
  {
    path: 'thirdPartyL3',
    component: OdbThirdPartyLevelThreePageComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
  },
  {
    path: 'thirdPartyL4',
    component: OdbThirdPartyLevel4PageComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
  },
  {
    path: 'deferralLanding',
    component: DeferralLandingPageComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
  },
  {
    path: 'billOptionsPaymentFacilitiesInfo',
    component: BillOptionsPaymentFacilitiesInfoPageComponent,
  },
  {
    path: 'deferralSelectDate',
    component: DeferralSelectDatePageComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
  },
  {
    path: 'deferralAcceptNewDate',
    component: DeferralAcceptNewDatePageComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
  },
  {
    path: 'deferralSuccessfulNewDate',
    component: DeferralSuccessfulNewDatePageComponent,
    canActivate: [MyProximusAuthenticationCheckGuard, CBUOnlyGuard],
  },
  {
    path: 'instalmentPlanDetails',
    component: MyBillInstalmentPlanDetailsPageComponent,
  },
  {
    path: 'expiredToken',
    component: ExpiredTokenComponent,
  },
  { path: '', redirectTo: 'overview', pathMatch: 'full' },
  { path: '**', redirectTo: 'overview' },
];

export const odbRoutePathList = ['cover', 'coverNoDetails'];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      initialNavigation: 'disabled', // the property to delay navigation
    }),
  ],

  exports: [RouterModule],
})
export class AppRoutingModule {}
